<template>
  <ngc-modal
    :open="value"
    size="fullscreen"
    variant="decide"
    :title.prop="header"
    data-testid="nav-dialog-modal"
    @close="handleClose"
  >
    <ngc-card
      elevated
      class="nav-dialog-card"
    >
      <div class="nav-dialog-body">
        <ngc-guided-nav
          v-if="showNav && guidedNav"
          class="nav-dialog-nav nav-dialog-guided-nav"
          :steps.prop="guidedNavSteps"
          :selected-step.prop="guidedNavSelectedStep"
          :forward-navigation-disabled.prop="forwardNavigationDisabled"
          :forward-navigation-disabled-error-message="forwardNavigationDisabledErrorMessage"
          @change="onGuidedNavChange"
        />
        <ngc-list
          v-else-if="showNav"
          class="nav-dialog-nav"
          selectable
          hide-checkmarks
        >
          <ngc-list-item
            v-for="item in navItems"
            :id="`nav-item-${item.key}`"
            :key="item.key"
            :value.prop="item.key"
            :text="item.label"
            :selected.prop="item.key === activeKey"
            hide-checkmark
            :class="{ 'nav-dialog-nav-item--indent': item.indent }"
            :data-testid="`nav-item-${item.key}`"
            @selected="selectNav(item.key)"
          >
            <ngc-icon
              slot="start"
              :name="item.icon"
            />
          </ngc-list-item>
        </ngc-list>

        <div class="nav-dialog-content">
          <div
            v-for="item in navItems"
            v-show="item.key === activeKey"
            :key="item.key"
            class="nav-dialog-panel"
          >
            <div
              v-if="item.title || item.label"
              class="nav-dialog-panel-header"
            >
              <h2 class="nav-dialog-panel-title">
                {{ item.title || item.label }}
              </h2>
              <span
                v-if="item.key === activeKey && subtitle"
                class="nav-dialog-panel-subtitle"
              >
                {{ subtitle }}
              </span>
            </div>

            <slot
              :name="item.key"
              :item="item"
            >
              <slot
                name="content"
                :item="item"
                :active-key="activeKey"
              />
            </slot>
          </div>
        </div>
      </div>
    </ngc-card>

    <div
      slot="footer"
      class="nav-dialog-footer"
    >
      <!-- size="large" is what ngc-modal gives its own footer buttons at
           size="fullscreen" (small->xlarge, standard->medium, fullscreen->large).
           Without it ngc-button applies no size class at all and the buttons
           render smaller than every other fullscreen modal. -->
      <ngc-button
        variant="secondary"
        size="large"
        :disabled="primaryLoading"
        data-testid="nav-dialog-cancel"
        @click="handleCancel"
      >
        Cancel
      </ngc-button>
      <ngc-button
        v-if="!hidePrimary"
        variant="primary"
        size="large"
        :loading="primaryLoading"
        :disabled="primaryDisabled"
        :data-cy="primaryCy || null"
        data-testid="nav-dialog-primary"
        @click="handlePrimary"
      >
        {{ primaryText }}
      </ngc-button>
    </div>
  </ngc-modal>
</template>

<script>
import { mapMutations } from 'vuex'

export default {
  name: 'EvNavDialog',
  props: {
    // Controls the modal open state (v-model).
    value: {
      type: Boolean,
      default: false,
      required: false
    },
    // Modal title shown centered in the header.
    header: {
      type: String,
      default: '',
      required: false
    },
    // Navigation points: [{ key, label, icon, title }].
    // `label` shows in the nav list; `title` (falls back to `label`) is the content heading.
    navItems: {
      type: Array,
      required: true,
      default: () => []
    },
    // The active nav key (supports :current-key.sync).
    currentKey: {
      type: [String, Number],
      default: null,
      required: false
    },
    // Optional subtitle shown under the content heading.
    subtitle: {
      type: String,
      default: '',
      required: false
    },
    primaryText: {
      type: String,
      default: 'Save',
      required: false
    },
    primaryDisabled: {
      type: Boolean,
      default: false,
      required: false
    },
    primaryLoading: {
      type: Boolean,
      default: false,
      required: false
    },
    hidePrimary: {
      type: Boolean,
      default: false,
      required: false
    },
    primaryCy: {
      type: String,
      default: '',
      required: false
    },
    // Use ngc-guided-nav (checkbox/checkmark stepper) instead of a plain list.
    guidedNav: {
      type: Boolean,
      default: false,
      required: false
    },
    forwardNavigationDisabled: {
      type: Boolean,
      default: false,
      required: false
    },
    forwardNavigationDisabledErrorMessage: {
      type: String,
      default: 'Finish this step before moving on',
      required: false
    }
  },
  data () {
    return {
      activeKey: this.currentKey != null ? this.currentKey : (this.navItems[0] && this.navItems[0].key),
      maxStepReached: this.currentKey != null ? this.currentKey : (this.navItems[0] && this.navItems[0].key)
    }
  },
  computed: {
    // With a single nav point there is nowhere to navigate, so the side nav is
    // just noise. Hide it and let the panel fill the card.
    showNav () {
      return this.navItems.length > 1
    },
    guidedNavSteps () {
      return this.navItems.map(item => ({
        id: String(item.key),
        text: item.label,
        value: String(item.key),
        complete: this.isGuidedStepComplete(item.key)
      }))
    },
    guidedNavSelectedStep () {
      return { step: String(this.activeKey), substep: null }
    }
  },
  watch: {
    currentKey (val) {
      if (val != null && val !== this.activeKey) {
        this.setActive(val)
      }
    },
    value (val) {
      if (val) {
        this.clearAutofocus()
        this.syncGuidedNavCurrentIcon()
      }
    },
    activeKey () {
      this.syncGuidedNavCurrentIcon()
    }
  },
  mounted () {
    this.syncGuidedNavCurrentIcon()
  },
  methods: {
    clearAutofocus () {
      // The native <dialog> auto-focuses the first focusable element on open,
      // which is often a text button (form inputs live in shadow DOM and get
      // skipped). Clear it so buttons don't show a focus ring on load; keyboard
      // focus continues to work normally. Runs after the modal's own rAF focus.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const active = document.activeElement
          if (active && active.tagName && active.tagName.toLowerCase() === 'ngc-button') {
            active.blur()
          }
        })
      })
    },
    // ngc-guided-nav only treats a step as "current" (arrow) when the previous
    // step is complete, so the first step is always a grey circle. Swap that
    // icon for the selected step.
    syncGuidedNavCurrentIcon () {
      if (!this.guidedNav) {
        return
      }
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const nav = this.$el && this.$el.querySelector('ngc-guided-nav')
            if (!nav || !nav.shadowRoot) {
              return
            }
            nav.shadowRoot.querySelectorAll('ngc-guided-nav-step').forEach(stepEl => {
              if (!stepEl.shadowRoot || !stepEl.step) {
                return
              }
              const icon = stepEl.shadowRoot.querySelector('ngc-icon')
              const listItem = stepEl.shadowRoot.querySelector('ngc-list-item')
              if (!icon) {
                return
              }
              const isSelected = String(stepEl.step.id) === String(this.activeKey)
              if (isSelected && icon.name === 'circle-filled') {
                icon.name = 'circle-arrow-right'
                if (listItem) {
                  listItem.classList.add('ngc-guided-nav-step-current')
                }
              }
            })
          })
        })
      })
    },
    ...mapMutations(['resetAppTimeout']),
    isGuidedStepComplete (key) {
      const keys = this.navItems.map(item => item.key)
      return keys.indexOf(key) < keys.indexOf(this.maxStepReached)
    },
    onGuidedNavChange (event) {
      const step = event.detail && event.detail.value && event.detail.value.step
      if (step == null) {
        return
      }
      const item = this.navItems.find(navItem => String(navItem.key) === String(step))
      if (item) {
        this.selectNav(item.key)
      }
    },
    selectNav (key) {
      if (key === this.activeKey) {
        return
      }
      this.setActive(key)
    },
    setActive (key) {
      this.activeKey = key
      const keys = this.navItems.map(item => item.key)
      if (keys.indexOf(key) > keys.indexOf(this.maxStepReached)) {
        this.maxStepReached = key
      }
      this.$emit('update:currentKey', key)
      this.resetAppTimeout()
    },
    handlePrimary () {
      this.$emit('primaryClick')
    },
    handleCancel () {
      this.handleClose()
    },
    handleClose () {
      // Emit a close *request* only. The parent owns `value` (v-model) and
      // decides whether to actually close (it may show a discard-changes
      // confirmation first), so we must not force it closed here.
      this.$emit('close')
    }
  }
}
</script>

<style scoped>

.nav-dialog-card {
  display: block;
  width: 1120px;
  max-width: 100%;
  margin: 48px auto;
  --ngc-card-width: 100%;
  --ngc-card-max-width: 100%;
  --ngc-card-border-radius: 16px;
}

.nav-dialog-body {
  display: flex;
  align-items: stretch;
  border-radius: 16px;
  overflow: hidden;
}

.nav-dialog-nav {
  flex: 0 0 280px;
  display: block;
  border-right: 1px solid var(--color-border-tertiary, #E5E5E5);
  --ngc-list-item-hover-background: var(--color-surface-brand, #EFF4F9);
  --ngc-list-item-selected-background: var(--color-surface-brand-secondary, #DCE8F2);
}

.nav-dialog-guided-nav {
  padding: 24px 16px;
}

/* --ngc-list-item-padding matches Gator's `comfortable` density. We set it
   through the variable rather than density="comfortable" because the density
   class sets `padding` directly in the shadow DOM, which leaves no way to
   override padding per item (see the indent rule below). */
.nav-dialog-nav ngc-list-item {
  --color-border-neutral: transparent;
  --ngc-list-item-padding: var(--spacing-8);
}

/* Must out-specify the `.nav-dialog-nav ngc-list-item` rule above. */
.nav-dialog-nav ngc-list-item.nav-dialog-nav-item--indent {
  --ngc-list-item-padding: var(--spacing-8) var(--spacing-8) var(--spacing-8) calc(var(--spacing-8) * 2);
}

.nav-dialog-content {
  flex: 1;
  min-width: 0;
  padding: 24px 32px;
}

.nav-dialog-panel-header {
  margin-bottom: 24px;
}

.nav-dialog-panel-title {
  font-size: 22px;
  font-weight: 600;
  line-height: 30px;
  margin: 0;
}

.nav-dialog-panel-subtitle {
  display: block;
  margin-top: 4px;
  font-size: 15px;
  color: var(--color-text-secondary, #6D6D6D);
}

/* Gator's own .gator-modal-action-buttons gaps at --spacing-5 (8px); UX wants
   16px between the footer buttons, so this deliberately deviates. Keep in step
   with addCompany's footer, which is the other fullscreen wizard. The
   .gator-modal-footer around this slot already centers the group. */
.nav-dialog-footer {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-8);
}

</style>
